import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { lastValueFrom, take } from 'rxjs';
import { AppResource } from 'src/app/app.resource';
import { PathType } from 'src/app/core/enums/path-type.enum';
import { NatureResidence, QualiteOccupant } from 'src/app/core/models/form-state.model';
import { Question } from 'src/app/core/models/question.model';
import { cloneResumeStep } from 'src/app/core/models/resume-step.model';
import { Step } from 'src/app/core/models/step.model';
import { Stepper } from 'src/app/core/models/stepper.model';
import { BaseComponent } from 'src/app/shared/components/base-component/base-component.component';
import { MatomoConstants } from 'src/app/shared/constants/matomo.constants';
import {
  HOUSE_APARTMENT_PATH,
  NUMBER_PIECES_PATH,
  ROOT_PATH
} from 'src/app/shared/constants/route.constants';
import * as fromRoot from '../../../core/state/core.state';
import * as fromHousing from '../../../core/state/housing';
import * as fromInfo from '../../../core/state/info';
import * as fromPath from '../../../core/state/path';

@Component({
  selector: 'app-primary-secondary',
  templateUrl: './primary-secondary.component.html',
  styleUrls: ['./primary-secondary.component.scss']
})
export class PrimarySecondaryComponent extends BaseComponent implements OnInit {
  formArray: FormArray<
    FormGroup<{
      id: FormControl<number>;
      isSelected: FormControl<boolean>;
      classIcon: FormControl<string>;
      label: FormControl<string>;
    }>
  > = new FormArray<
    FormGroup<{
      id: FormControl<number>;
      isSelected: FormControl<boolean>;
      classIcon: FormControl<string>;
      label: FormControl<string>;
    }>
  >([]);

  override step: Step = {
    step: this.resource.header.stepHousing,
    stepNumber: 1,
    subStep: this.resource.header.subStepPrimarySecondary,
    subStepNumber: 4
  };

  displayModal: boolean = false;
  override question: Question = {
    libelle: this.resource.question.primarySecondary,
    popupInfo: undefined
  };
  housingData!: fromHousing.HousingState;
  path!: PathType;

  public override currentPage = MatomoConstants.RESIDENCE;

  constructor(store: Store<fromRoot.State>, resources: AppResource, private router: Router) {
    super(store, resources);
  }

  override async ngOnInit() {
    super.ngOnInit();
    this.initForm();
    this.updateResumeStepVisibility(true);
    this.loadStateData();
  }

  private async loadStateData() {
    this.housingData = await lastValueFrom(
      this.store.select(fromHousing.selectHousing).pipe(take(1))
    );

    this.path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));

    switch (this.housingData.SaisieHabitation.NatureResidence) {
      case NatureResidence.Principale:
        this.formArray.controls
          .find(e => e.controls.id.value === 1)!
          .controls.isSelected.setValue(true);
        this.formArray.controls
          .find(e => e.controls.id.value !== 1)!
          .controls.isSelected.setValue(false);
        break;
      case NatureResidence.Secondaire:
        this.formArray.controls
          .find(e => e.controls.id.value === 2)!
          .controls.isSelected.setValue(true);
        this.formArray.controls
          .find(e => e.controls.id.value !== 2)!
          .controls.isSelected.setValue(false);
        break;
      case NatureResidence.ForRent:
        this.formArray.controls
          .find(e => e.controls.id.value === 3)!
          .controls.isSelected.setValue(true);
        this.formArray.controls
          .find(e => e.controls.id.value !== 3)!
          .controls.isSelected.setValue(false);
        break;
      default:
        break;
    }
  }

  handleClickFromButtons(id: number) {
    switch (id) {
      case 1:
        this.formArray.controls
          .find(e => e.controls.id.value === 1)!
          .controls.isSelected.setValue(true);
        this.onPrimarySelected();
        break;
      case 2:
        this.formArray.controls
          .find(e => e.controls.id.value === 2)!
          .controls.isSelected.setValue(true);
        this.onSecondarySelected();
        break;
      case 3:
        this.formArray.controls
          .find(e => e.controls.id.value === 3)!
          .controls.isSelected.setValue(true);
        this.onTertiarySelected();
        break;
    }
  }

  async onTertiarySelected() {
    this.store.dispatch(
      fromHousing.patchHousingNature({
        payload: { NatureResidence: NatureResidence.ForRent }
      })
    );

    this.path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));
    if (this.path != PathType.HOUSING) {
      this.store.dispatch(
        fromPath.updatePreviousType({
          previousPathType: this.path
        })
      );
    }
    this.store.dispatch(
      fromPath.updateType({
        pathType: PathType.HOUSING
      })
    );
    // DISPLAY ERROR MODAL
    this.displayModal = true;
  }

  async onPrimarySelected() {
    this.store.dispatch(
      fromHousing.patchHousingNature({
        payload: { NatureResidence: NatureResidence.Principale }
      })
    );

    let res = await lastValueFrom(
      this.store.select(fromHousing.selectHousingOccupantType).pipe(take(1))
    )!;

    let previousPath = await lastValueFrom(
      this.store.select(fromPath.selectPreviousPathType).pipe(take(1))
    );

    if (
      res == QualiteOccupant.Locataire &&
      previousPath &&
      previousPath != PathType.HOUSING &&
      previousPath != PathType.HOUSING_ETUDIANTE
    ) {
      this.store.dispatch(
        fromPath.updateType({
          pathType: previousPath
        })
      );
    }

    this.nextStep();
  }

  private initForm() {
    const elements = [
      { id: 1, classIcon: 'primary', label: this.resource.primarysecondary.primary },
      { id: 2, classIcon: 'secondary', label: this.resource.primarysecondary.secondary },
      { id: 3, classIcon: 'tertiary', label: this.resource.primarysecondary.tertiary }
    ];

    elements.forEach(element => {
      this.formArray.push(
        new FormGroup({
          id: new FormControl(element.id, { nonNullable: true }),
          isSelected: new FormControl(false, { nonNullable: true }),
          classIcon: new FormControl(element.classIcon, { nonNullable: true }),
          label: new FormControl(element.label, { nonNullable: true })
        })
      );
    });
  }

  async onSecondarySelected() {
    this.store.dispatch(
      fromHousing.patchHousingNature({
        payload: { NatureResidence: NatureResidence.Secondaire }
      })
    );

    this.path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));
    if (this.path != PathType.HOUSING) {
      this.store.dispatch(
        fromPath.updatePreviousType({
          previousPathType: this.path
        })
      );
    }
    this.store.dispatch(
      fromPath.updateType({
        pathType: PathType.HOUSING
      })
    );
    this.nextStep();
  }

  async nextStep() {
    this.path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));
    this.housingData = await lastValueFrom(
      this.store.select(fromHousing.selectHousing).pipe(take(1))
    );
    await this.updateResumeStep();

    await this.updateStepper(true);
    this.router.navigate([`${ROOT_PATH}${HOUSE_APARTMENT_PATH}`]);
  }

  async updateStepper(preferentialrate: boolean) {
    let stepper = await lastValueFrom(this.store.select(fromInfo.selectStepper).pipe(take(1)));
    let tmpStepper = JSON.parse(JSON.stringify(stepper)) as Stepper;

    let title = tmpStepper.devis.titles.find(
      (x: { libelle: string }) => x.libelle == this.resource.stepper.lodging
    );

    let subtitleS = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.primarysecondary
    );

    if (subtitleS) subtitleS.isValid = true;

    let subtitleN;
    if (preferentialrate) {
      subtitleN = title?.subtitles.find(
        (x: { url: string }) => x.url == this.resource.stepper.preferentialrate
      );
    } else {
      subtitleN = title?.subtitles.find(
        (x: { url: string }) => x.url == this.resource.stepper.numberpieces
      );
    }

    if (subtitleN) {
      subtitleN.isActive = true;
      subtitleN.isVisible = true;
    }

    this.store.dispatch(fromInfo.updateStepper({ payload: tmpStepper }));
  }

  async updateResumeStep() {
    const resumeStep = await lastValueFrom(
      this.store.select(fromInfo.selectCurrentResumeStep).pipe(take(1))
    );

    const tmpResumeStep = cloneResumeStep(resumeStep);

    const stepResumeStep = tmpResumeStep.steps?.find(
      x => x.id == this.resource.resumestep.primarysecondary
    );
    let libelle = '';

    if (this.formArray.controls.find(e => e.controls.id.value === 1)!.controls.isSelected)
      libelle = this.resource.resumestep.primary;
    else if (this.formArray.controls.find(e => e.controls.id.value === 2)!.controls.isSelected)
      libelle = this.resource.resumestep.secondary;

    if (stepResumeStep && libelle != '') {
      stepResumeStep.step = libelle;
      stepResumeStep.isVisible = true;
    } else {
      tmpResumeStep.steps?.push({
        id: this.resource.resumestep.primarysecondary,
        step: libelle,
        url: this.resource.resumestep.primarysecondary,
        isVisible: true
      });
    }
    this.store.dispatch(fromInfo.updateResumeStep({ payload: tmpResumeStep }));
  }

  setClassActive(value: boolean): string {
    return value ? 'active' : 'inactive';
  }
}
